import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faSync,
  faCoins,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { cryptoApi } from '../api/cryptoApi';
import { orderApi } from '../api/orderApi';
import { accountApi } from '../api/accountApi';
import { useAuth } from '../contexts/AuthContext';
import { useRisk } from '../contexts/RiskContext';
import CryptoBarsChart from '../components/CryptoBarsChart';

const SYMBOL_KEY = 'lt-crypto-symbol';
const QUOTE_FILTERS = ['USD', 'USDT', 'USDC', 'ALL'];
const OPEN_STATUSES = new Set(['new', 'accepted', 'pending_new', 'pending_replace', 'pending_cancel']);

function priceDigits(n) {
  const abs = Math.abs(Number(n) || 0);
  if (abs >= 1000) return 2;
  if (abs >= 1) return 4;
  if (abs >= 0.01) return 6;
  return 8;
}

function formatPrice(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  const d = priceDigits(v);
  return v.toLocaleString(undefined, { minimumFractionDigits: Math.min(2, d), maximumFractionDigits: d });
}

function formatQty(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 8 });
}

function formatUsd(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n));
}

function formatPct(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
}

function tone(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return 'text-muted';
  return v > 0 ? 'text-bull' : 'text-bear';
}

const CryptoTradingPage = () => {
  const { maskAmounts } = useAuth();
  const { checkOrder } = useRisk();
  const money = (v) => (maskAmounts ? '••••' : formatUsd(v));

  const [assets, setAssets] = useState([]);
  const [snapshots, setSnapshots] = useState({});
  const [symbol, setSymbol] = useState(() => localStorage.getItem(SYMBOL_KEY) || 'BTC/USD');
  const [search, setSearch] = useState('');
  const [quoteFilter, setQuoteFilter] = useState('USD');
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(true);
  const [loadingBook, setLoadingBook] = useState(false);
  const [error, setError] = useState(null);
  const [closing, setClosing] = useState(null);
  const [canceling, setCanceling] = useState(null);

  const [side, setSide] = useState('buy');
  const [orderBy, setOrderBy] = useState('qty');
  const [qty, setQty] = useState('');
  const [notional, setNotional] = useState('100');
  const [orderType, setOrderType] = useState('market');
  const [timeInForce, setTimeInForce] = useState('gtc');
  const [limitPrice, setLimitPrice] = useState('');
  const [stopPrice, setStopPrice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [ticketMsg, setTicketMsg] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const selectedAsset = useMemo(
    () => assets.find((a) => a.symbol === symbol) || null,
    [assets, symbol]
  );
  const quote = snapshots[symbol] || null;
  const last = quote?.last || quote?.mid || null;

  const filteredAssets = useMemo(() => {
    const q = search.trim().toUpperCase();
    return assets.filter((a) => {
      if (quoteFilter !== 'ALL' && a.quote !== quoteFilter) return false;
      if (!q) return true;
      return a.symbol.includes(q) || String(a.name || '').toUpperCase().includes(q) || a.base.includes(q);
    });
  }, [assets, search, quoteFilter]);

  const selectSymbol = useCallback((next) => {
    const raw = String(next || '').toUpperCase();
    const match = assets.find(
      (a) => a.symbol === raw || a.symbol.replace('/', '') === raw.replace('/', '')
    );
    const value = match?.symbol || raw;
    setSymbol(value);
    localStorage.setItem(SYMBOL_KEY, value);
    setTicketMsg(null);
  }, [assets]);

  const loadAssets = useCallback(async () => {
    setLoadingAssets(true);
    try {
      const res = await cryptoApi.getAssets();
      const list = res.data || [];
      setAssets(list);
      setSymbol((current) => {
        if (current && list.some((a) => a.symbol === current)) return current;
        const btc = list.find((a) => a.symbol === 'BTC/USD');
        const next = btc?.symbol || list[0]?.symbol || 'BTC/USD';
        localStorage.setItem(SYMBOL_KEY, next);
        return next;
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load crypto assets');
    } finally {
      setLoadingAssets(false);
    }
  }, []);

  const loadMarket = useCallback(async (pairs) => {
    const symbols = pairs?.length ? pairs : assets.map((a) => a.symbol);
    if (!symbols.length) return;
    try {
      const res = await cryptoApi.getSnapshots(symbols);
      setSnapshots(res.data || {});
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Crypto snapshots failed', err);
    }
  }, [assets]);

  const loadBook = useCallback(async (show = false) => {
    if (show) setLoadingBook(true);
    try {
      const [posRes, orderRes, acctRes] = await Promise.all([
        cryptoApi.getPositions(),
        cryptoApi.getOrders({ status: 'open', limit: 50 }),
        accountApi.getAccount().catch(() => null),
      ]);
      setPositions(posRes.data || []);
      setOrders((orderRes.data || []).filter((o) => OPEN_STATUSES.has(String(o.status || '').toLowerCase())));
      if (acctRes?.data) setAccount(acctRes.data);
    } catch (err) {
      console.error('Crypto book failed', err);
    } finally {
      if (show) setLoadingBook(false);
    }
  }, []);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  useEffect(() => {
    if (!assets.length) return undefined;
    loadMarket(assets.map((a) => a.symbol));
    loadBook(true);
    const marketTimer = setInterval(() => loadMarket(assets.map((a) => a.symbol)), 5000);
    const bookTimer = setInterval(() => loadBook(false), 12000);
    return () => {
      clearInterval(marketTimer);
      clearInterval(bookTimer);
    };
  }, [assets, loadMarket, loadBook]);

  useEffect(() => {
    if (!quote) return;
    if (orderType !== 'market' && !limitPrice) {
      setLimitPrice(quote.ask || quote.last ? String(quote.ask || quote.last) : '');
    }
  }, [symbol]); // eslint-disable-line react-hooks/exhaustive-deps

  const estimatedNotional = useMemo(() => {
    if (orderBy === 'notional') return Number(notional) || 0;
    const px = orderType === 'limit' && Number(limitPrice) ? Number(limitPrice) : last;
    return (Number(qty) || 0) * (Number(px) || 0);
  }, [orderBy, notional, qty, orderType, limitPrice, last]);

  const submitOrder = async (event) => {
    event.preventDefault();
    setTicketMsg(null);
    if (!symbol) return;
    const orderData = {
      symbol,
      side,
      type: orderType,
      timeInForce,
    };
    if (orderBy === 'notional') {
      const n = Number(notional);
      if (!Number.isFinite(n) || n <= 0) {
        setTicketMsg({ type: 'error', text: 'Enter a dollar amount' });
        return;
      }
      orderData.notional = n;
    } else {
      const q = Number(qty);
      if (!Number.isFinite(q) || q <= 0) {
        setTicketMsg({ type: 'error', text: 'Enter a quantity' });
        return;
      }
      orderData.qty = q;
    }
    if (orderType === 'limit' || orderType === 'stop_limit') {
      const lp = Number(limitPrice);
      if (!Number.isFinite(lp) || lp <= 0) {
        setTicketMsg({ type: 'error', text: 'Limit price is required' });
        return;
      }
      orderData.limitPrice = lp;
    }
    if (orderType === 'stop_limit') {
      const sp = Number(stopPrice);
      if (!Number.isFinite(sp) || sp <= 0) {
        setTicketMsg({ type: 'error', text: 'Stop price is required' });
        return;
      }
      orderData.stopPrice = sp;
    }

    try {
      setSubmitting(true);
      const risk = await checkOrder({ ...orderData, estimatedNotional });
      if (risk.blocks?.length) {
        setTicketMsg({ type: 'error', text: `Risk block: ${risk.blocks.join(' · ')}` });
        return;
      }
      if (risk.warnings?.length) {
        const ok = window.confirm(`${risk.warnings.join('\n')}\n\nSubmit anyway?`);
        if (!ok) return;
      }
      const result = await orderApi.submitOrder(orderData);
      setTicketMsg({
        type: 'ok',
        text: `${side.toUpperCase()} ${symbol} ${result.data?.status || 'submitted'}`,
      });
      setQty('');
      loadBook(false);
    } catch (err) {
      setTicketMsg({ type: 'error', text: err.response?.data?.error || err.message || 'Order failed' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = async (posSymbol) => {
    setClosing(posSymbol);
    try {
      await cryptoApi.closePosition(posSymbol);
      await loadBook(false);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setClosing(null);
    }
  };

  const handleCancel = async (orderId) => {
    setCanceling(orderId);
    try {
      await orderApi.cancelOrder(orderId);
      await loadBook(false);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setCanceling(null);
    }
  };

  const spreadPct = quote?.mid && quote?.spread ? (quote.spread / quote.mid) * 100 : null;
  const posForSymbol = positions.find((p) => p.symbol === symbol || p.symbol?.replace('/', '') === symbol.replace('/', ''));

  return (
    <main className="flex-1 container mx-auto px-3 lg:px-5 py-4 space-y-3">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-muted">
            <FontAwesomeIcon icon={faCoins} className="text-[var(--lt-warning)]" />
            Alpaca crypto
            <span className="inline-flex items-center gap-1 text-bull normal-case tracking-normal">
              <span className="h-1.5 w-1.5 rounded-full bg-bull animate-pulse" />
              24/7
            </span>
          </div>
          <h1 className="text-xl font-semibold text-ink mt-0.5">Crypto desk</h1>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-[11px]">
          <div>
            <div className="lt-label">Cash</div>
            <div className="num text-ink">{money(account?.cash)}</div>
          </div>
          <div>
            <div className="lt-label">Buying power</div>
            <div className="num text-ink">{money(account?.buyingPower)}</div>
          </div>
          <div>
            <div className="lt-label">Crypto positions</div>
            <div className="num text-ink">{positions.length}</div>
          </div>
          <button
            type="button"
            onClick={() => {
              loadMarket(assets.map((a) => a.symbol));
              loadBook(true);
            }}
            className="px-2.5 py-1.5 rounded border border-hairline text-muted hover:text-ink hover:bg-elevated"
            title="Refresh"
          >
            <FontAwesomeIcon icon={faSync} className={loadingBook ? 'fa-spin' : ''} />
          </button>
        </div>
      </header>

      {error && (
        <div className="lt-card px-3 py-2 text-sm text-bear flex items-center justify-between gap-3">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} className="text-muted hover:text-ink">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)_280px] gap-3 items-start">
        <section className="lt-card overflow-hidden flex flex-col min-h-[280px] lg:min-h-[520px]">
          <div className="px-3 py-2 border-b border-hairline space-y-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search BTC, ETH, SOL…"
              className="w-full bg-[var(--lt-input)] border border-hairline rounded px-2.5 py-1.5 text-sm text-ink placeholder:text-muted"
            />
            <div className="flex gap-1">
              {QUOTE_FILTERS.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setQuoteFilter(q)}
                  className={`flex-1 py-1 text-[10px] uppercase tracking-wide rounded ${
                    quoteFilter === q ? 'bg-elevated text-ink' : 'text-muted hover:text-ink'
                  }`}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto max-h-[36vh] lg:max-h-[70vh]">
            {loadingAssets ? (
              <div className="py-10 text-center text-muted">
                <FontAwesomeIcon icon={faSpinner} spin />
              </div>
            ) : (
              filteredAssets.map((asset) => {
                const snap = snapshots[asset.symbol];
                const pct = snap?.change24hPct;
                const active = asset.symbol === symbol;
                return (
                  <button
                    key={asset.symbol}
                    type="button"
                    onClick={() => selectSymbol(asset.symbol)}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left border-b border-hairline ${
                      active ? 'bg-elevated' : 'hover:bg-elevated'
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="num text-sm text-ink truncate">{asset.symbol}</div>
                      <div className="text-[10px] text-muted truncate">{asset.name}</div>
                    </div>
                    <div className="text-right">
                      <div className="num text-sm">{formatPrice(snap?.last)}</div>
                      <div className={`num text-[10px] ${tone(pct)}`}>{formatPct(pct)}</div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </section>

        <section className="space-y-3 min-w-0">
          <div className="lt-card px-4 py-3">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <div className="lt-label">{selectedAsset?.name || 'Pair'}</div>
                <div className="text-2xl font-semibold num tracking-tight">{symbol}</div>
              </div>
              <div className="text-right">
                <div className={`text-2xl num font-semibold ${tone(quote?.change24h)}`}>
                  {formatPrice(last)}
                </div>
                <div className={`text-xs num ${tone(quote?.change24hPct)}`}>
                  {quote?.change24h >= 0 ? '+' : ''}
                  {formatPrice(quote?.change24h)} ({formatPct(quote?.change24hPct)})
                </div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
              <div>
                <div className="lt-label">Bid</div>
                <div className="num text-bull">{formatPrice(quote?.bid)}</div>
              </div>
              <div>
                <div className="lt-label">Ask</div>
                <div className="num text-bear">{formatPrice(quote?.ask)}</div>
              </div>
              <div>
                <div className="lt-label">Spread</div>
                <div className="num">
                  {formatPrice(quote?.spread)} {spreadPct != null ? `(${spreadPct.toFixed(3)}%)` : ''}
                </div>
              </div>
              <div>
                <div className="lt-label">24h range</div>
                <div className="num">
                  {formatPrice(quote?.low24h)} – {formatPrice(quote?.high24h)}
                </div>
              </div>
            </div>
            {posForSymbol && (
              <div className="mt-3 text-[11px] text-muted">
                Open {posForSymbol.side} {formatQty(posForSymbol.qty)} · {money(posForSymbol.marketValue)} ·
                <span className={`ml-1 ${tone(posForSymbol.unrealizedPl)}`}>
                  {money(posForSymbol.unrealizedPl)} ({formatPct((posForSymbol.unrealizedPlpc || 0) * 100)})
                </span>
              </div>
            )}
          </div>

          <div className="lt-card overflow-hidden">
            <CryptoBarsChart symbol={symbol} height={380} />
          </div>
        </section>

        <section className="lt-card p-3 space-y-3">
          <div className="lt-label">Order ticket</div>
          <div className="grid grid-cols-2 gap-1 p-0.5 rounded bg-elevated">
            {['buy', 'sell'].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSide(s)}
                className={`py-2 text-sm font-semibold uppercase tracking-wide rounded ${
                  side === s
                    ? s === 'buy'
                      ? 'bg-bull text-[#0B0E14]'
                      : 'bg-bear text-white'
                    : 'text-muted hover:text-ink'
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          <form onSubmit={submitOrder} className="space-y-3">
            <div className="grid grid-cols-2 gap-1 text-[11px]">
              <button
                type="button"
                onClick={() => setOrderBy('qty')}
                className={`py-1.5 rounded border ${orderBy === 'qty' ? 'border-action text-ink' : 'border-hairline text-muted'}`}
              >
                Qty
              </button>
              <button
                type="button"
                onClick={() => setOrderBy('notional')}
                className={`py-1.5 rounded border ${orderBy === 'notional' ? 'border-action text-ink' : 'border-hairline text-muted'}`}
              >
                $ Notional
              </button>
            </div>

            {orderBy === 'qty' ? (
              <label className="block">
                <div className="lt-label mb-1">Quantity</div>
                <input
                  type="number"
                  step="any"
                  min="0"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  className="w-full bg-[var(--lt-input)] border border-hairline rounded px-2.5 py-2 num text-ink"
                  placeholder={selectedAsset?.minOrderSize ? String(selectedAsset.minOrderSize) : '0.001'}
                />
              </label>
            ) : (
              <label className="block">
                <div className="lt-label mb-1">USD amount</div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={notional}
                  onChange={(e) => setNotional(e.target.value)}
                  className="w-full bg-[var(--lt-input)] border border-hairline rounded px-2.5 py-2 num text-ink"
                />
              </label>
            )}

            <label className="block">
              <div className="lt-label mb-1">Type</div>
              <select
                value={orderType}
                onChange={(e) => setOrderType(e.target.value)}
                className="w-full bg-[var(--lt-input)] border border-hairline rounded px-2.5 py-2 text-sm text-ink"
              >
                <option value="market">Market</option>
                <option value="limit">Limit</option>
                <option value="stop_limit">Stop limit</option>
              </select>
            </label>

            {(orderType === 'limit' || orderType === 'stop_limit') && (
              <label className="block">
                <div className="lt-label mb-1">Limit price</div>
                <input
                  type="number"
                  step="any"
                  value={limitPrice}
                  onChange={(e) => setLimitPrice(e.target.value)}
                  className="w-full bg-[var(--lt-input)] border border-hairline rounded px-2.5 py-2 num text-ink"
                />
              </label>
            )}
            {orderType === 'stop_limit' && (
              <label className="block">
                <div className="lt-label mb-1">Stop price</div>
                <input
                  type="number"
                  step="any"
                  value={stopPrice}
                  onChange={(e) => setStopPrice(e.target.value)}
                  className="w-full bg-[var(--lt-input)] border border-hairline rounded px-2.5 py-2 num text-ink"
                />
              </label>
            )}

            <label className="block">
              <div className="lt-label mb-1">Time in force</div>
              <select
                value={timeInForce}
                onChange={(e) => setTimeInForce(e.target.value)}
                className="w-full bg-[var(--lt-input)] border border-hairline rounded px-2.5 py-2 text-sm text-ink"
              >
                <option value="gtc">GTC</option>
                <option value="ioc">IOC</option>
              </select>
            </label>

            <div className="flex items-center justify-between text-[11px] text-muted">
              <span>Est. notional</span>
              <span className="num text-ink">{money(estimatedNotional)}</span>
            </div>
            {selectedAsset?.minOrderSize != null && (
              <div className="text-[10px] text-muted">Min size {formatQty(selectedAsset.minOrderSize)}</div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className={`w-full py-2.5 rounded font-semibold uppercase tracking-wide text-sm ${
                side === 'buy' ? 'bg-bull text-[#0B0E14]' : 'bg-bear text-white'
              } disabled:opacity-60`}
            >
              {submitting ? <FontAwesomeIcon icon={faSpinner} spin /> : `${side} ${symbol}`}
            </button>
            {ticketMsg && (
              <div className={`text-[12px] ${ticketMsg.type === 'ok' ? 'text-bull' : 'text-bear'}`}>
                {ticketMsg.text}
              </div>
            )}
            <p className="text-[10px] text-muted leading-snug">
              Alpaca crypto supports market, limit, and stop-limit with GTC or IOC. Pairs trade continuously — no session hours.
            </p>
          </form>
        </section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <section className="lt-card overflow-hidden">
          <div className="px-3 py-2 border-b border-hairline flex items-center justify-between">
            <div className="lt-label">Positions</div>
            <span className="text-[10px] text-muted">{positions.length}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left px-3 py-2">Pair</th>
                  <th className="text-right px-3 py-2">Qty</th>
                  <th className="text-right px-3 py-2">Mkt</th>
                  <th className="text-right px-3 py-2">P/L</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {positions.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-muted text-xs">
                      No crypto positions
                    </td>
                  </tr>
                )}
                {positions.map((pos) => (
                  <tr key={pos.symbol} className="border-t border-hairline hover:bg-elevated">
                    <td className="px-3 py-2">
                      <button type="button" className="num text-action" onClick={() => selectSymbol(pos.symbol.includes('/') ? pos.symbol : pos.symbol)}>
                        {pos.symbol}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right num">{formatQty(pos.qty)}</td>
                    <td className="px-3 py-2 text-right num">{money(pos.marketValue)}</td>
                    <td className={`px-3 py-2 text-right num ${tone(pos.unrealizedPl)}`}>
                      {money(pos.unrealizedPl)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => handleClose(pos.symbol)}
                        disabled={closing === pos.symbol}
                        className="text-[10px] uppercase tracking-wide text-bear hover:underline disabled:opacity-50"
                      >
                        {closing === pos.symbol ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Close'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="lt-card overflow-hidden">
          <div className="px-3 py-2 border-b border-hairline flex items-center justify-between">
            <div className="lt-label">Open orders</div>
            <span className="text-[10px] text-muted">
              {lastRefresh ? lastRefresh.toLocaleTimeString() : '—'}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left px-3 py-2">Pair</th>
                  <th className="text-left px-3 py-2">Side</th>
                  <th className="text-right px-3 py-2">Qty</th>
                  <th className="text-right px-3 py-2">Type</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-muted text-xs">
                      No open crypto orders
                    </td>
                  </tr>
                )}
                {orders.map((order) => (
                  <tr key={order.id} className="border-t border-hairline">
                    <td className="px-3 py-2 num">
                      <button type="button" className="text-action" onClick={() => selectSymbol(order.symbol)}>
                        {order.symbol}
                      </button>
                    </td>
                    <td className={`px-3 py-2 uppercase text-[11px] ${order.side === 'buy' ? 'text-bull' : 'text-bear'}`}>
                      {order.side}
                    </td>
                    <td className="px-3 py-2 text-right num">{formatQty(order.qty || order.notional)}</td>
                    <td className="px-3 py-2 text-right text-[11px] text-muted">
                      {order.type} {order.limitPrice != null ? `@ ${formatPrice(order.limitPrice)}` : ''}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => handleCancel(order.id)}
                        disabled={canceling === order.id}
                        className="text-[10px] uppercase tracking-wide text-muted hover:text-bear disabled:opacity-50"
                      >
                        {canceling === order.id ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Cancel'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </main>
  );
};

export default CryptoTradingPage;
